import { merge } from "lodash";
import { DateTimeUnit, Duration } from "luxon";
import { Zoom, ZoomSpanLimits } from "shared-ts-mui";
import { DurationSeriesMuiChartProps } from "./DurationSeriesMuiChart";

export interface TimeAxisZoomOptions {
  preferredTicks: number;
  minTickSize: number;
  maxTickSize: number;
  fixedZoom?: boolean;
}

const defaultZoomOptions: TimeAxisZoomOptions = {
  preferredTicks: 12,
  minTickSize: 100,
  maxTickSize: 200,
  fixedZoom: false,
};

export const resolveTimeAxisZoom = (
  xAxisMin: Duration,
  xAxisMax: Duration,
  xAxisZoom: DurationSeriesMuiChartProps["xAxisZoom"],
  timeUnit: DateTimeUnit,
  xAxisWidth: number | undefined,
  zoomAlignment: "start" | "end" = "start"
): Zoom | undefined => {
  if (
    xAxisWidth === undefined ||
    !(xAxisZoom === true || xAxisZoom !== undefined)
  )
    return undefined;

  const options: TimeAxisZoomOptions = merge(
    {},
    defaultZoomOptions,
    typeof xAxisZoom === "object" ? xAxisZoom : undefined
  );

  // Calculate total ticks in the range (ceil to include partial)
  const totalSpanAsDuration = xAxisMax.minus(xAxisMin).shiftToAll();
  const totalTicks = Math.max(
    1,
    Math.ceil(totalSpanAsDuration.as(timeUnit) ?? 0)
  );

  // Determine how many ticks can fit while keeping px per unit >= minUnitTickSize
  const minTickPx = Math.max(1, options.minTickSize);
  const ticksThatFitAtMin = Math.max(1, Math.floor(xAxisWidth / minTickPx));

  // Fit as many ticks as possible without dropping below minUnitTickSize
  const ticksToShow = Math.min(totalTicks, ticksThatFitAtMin);

  let start: number;
  let end: number;

  if (ticksToShow >= totalTicks) {
    // Everything fits: show full range
    start = 0;
    end = 100;
  } else if (zoomAlignment === "start") {
    // Align zoom window to the beginning of the range
    start = 0;
    end = Math.max(0, Math.min(100, (ticksToShow / totalTicks) * 100));
  } else {
    // Align zoom window to the end of the range
    start = Math.max(0, Math.min(100, 100 - (ticksToShow / totalTicks) * 100));
    end = 100;
  }

  return {
    start,
    end,
  };
};

/**
 * Compute minSpan and maxSpan ensuring:
 * - maxSpan respects minUnitTickSize (px per unit >= minUnitTickSize)
 * - minSpan respects maxUnitTickSize (px per unit <= maxUnitTickSize)
 */
export const resolveTimeAxisZoomSpanLimits = (
  min: Duration,
  max: Duration,
  timeUnit: DateTimeUnit,
  width: number | undefined,
  zoom?: DurationSeriesMuiChartProps["xAxisZoom"],
  zoomAlignment: "start" | "end" = "start"
): ZoomSpanLimits | undefined => {
  if (zoom === undefined || zoom === false || width === undefined || width <= 0)
    return undefined;

  const options: TimeAxisZoomOptions = merge(
    {},
    defaultZoomOptions,
    typeof zoom === "object" ? zoom : undefined
  );

  if (options.fixedZoom !== true) return undefined;

  // Base unit: months (band axis groups by months)
  const totalSpanAsDuration = max.minus(min).shiftToAll();
  const totalTicks = Math.max(
    1,
    Math.ceil(totalSpanAsDuration.as(timeUnit) ?? 0)
  );

  const minTickPx = Math.max(1, options.minTickSize);
  const maxTickPx = Math.max(1, options.maxTickSize);

  // Compute unit counts to satisfy pixel constraints
  const unitsForMinTick = Math.max(1, Math.floor(width / minTickPx)); // px/unit >= minTickPx
  const unitsForMaxTick = Math.max(1, Math.ceil(width / maxTickPx)); // px/unit <= maxTickPx

  // Convert to spans in percentage
  let maxSpan = Math.min(
    100,
    (Math.min(totalTicks, unitsForMinTick) / totalTicks) * 100
  );
  let minSpan = Math.min(
    100,
    (Math.min(totalTicks, unitsForMaxTick) / totalTicks) * 100
  );

  // Ensure ordering: minSpan should not exceed maxSpan
  if (minSpan > maxSpan) {
    minSpan = maxSpan;
  }

  // Guard against NaN or invalid values
  if (!isFinite(minSpan)) minSpan = 0;
  if (!isFinite(maxSpan)) maxSpan = 100;

  return { minSpan, maxSpan };
};
